import { Component, inject, signal, computed, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthenticationService } from '@shared/authentication.service';
import { Ad6Constants } from '@shared/constants';
import { AppAbility, AppSubjects } from '@shared/permissions/types'; // Pas pad aan indien nodig

type Ad6MenuItem = {
  code: string;
  label: string;
  icon?: string;
  data?: string[];
  subject?: AppSubjects; // <-- Koppel het menu item aan je CASL subject
};

@Component({
  selector: 'ad6-sidebar',
  imports: [MatListModule, MatIcon, TranslatePipe, MatMenuModule, MatButtonModule],
  styleUrl: './sidebar.component.scss',
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private abilityService = inject<AbilityServiceSignal<AppAbility>>(AbilityServiceSignal);

  sidenavOpen = input(true);

  auth = this.authenticationService;
  user = signal(this.auth.getUser());

  userName = computed(() => {
    const profile = this.user()?.profile;
    return profile?.name ?? profile?.preferred_username ?? '';
  });

  userEmail = computed(() => this.user()?.profile?.email ?? '');

  userAvatar = computed(() => {
    const profile = this.user()?.profile;
    const picture = typeof profile?.picture === 'string' ? profile.picture : null;
    const avatar = typeof profile?.['avatar'] === 'string' ? profile['avatar'] : null;
    return picture ?? avatar ?? null;
  });

  userRole = computed(() => {
    const roles = (this.user()?.profile?.['group_membership'] ?? []) as string[];
    if (roles.includes(Ad6Constants.ROLE_ADMINISTRATORS)) {
      return 'general.role.administrator';
    }
    if (roles.includes(Ad6Constants.ROLE_AD6USER)) {
      return 'general.role.AD6User';
    }
    if (roles.includes(Ad6Constants.ROLE_GUEST)) {
      return 'general.role.guest';
    }
    return '';
  });

  // 1. Definieer je statische, volledige lijst inclusief de benodigde subjects
  private baseMenuItems: Ad6MenuItem[] = [
    { code: 'config', label: 'config.list.title', data: ['config'], icon: 'settings', subject: 'config' },
    { code: 'events', label: 'event.list.title', data: ['event'], icon: 'event', subject: 'event' },
    {
      code: 'participants',
      label: 'participant.list.title',
      data: ['participant'],
      icon: 'person',
      subject: 'participant',
    },
    { code: 'teams', label: 'team.list.title', data: ['team'], icon: 'groups', subject: 'team' },
    {
      code: 'pushmessages',
      label: 'push-message.list.title',
      data: ['pushmessage'],
      icon: 'send',
      subject: 'pushMessage',
    },
    {
      code: 'translations',
      label: 'translation.list.title',
      data: ['translation'],
      icon: 'translate',
      subject: 'translation',
    },
    {
      code: 'image-management',
      label: 'image-management.title',
      data: ['image-management'],
      icon: 'image',
      subject: 'imageManagement',
    },
    {
      code: 'taskmanagement',
      label: 'Taakbeheer',
      data: ['taskmanagement'],
      icon: 'settings',
      subject: 'taskManagement',
    },
  ];

  // 2. Maak het dynamisch met computed.
  // Dit reageert direct op wijzigingen in AbilityServiceSignal.
  menuItems = computed(() => {
    return this.baseMenuItems.filter(item => {
      // Als er geen subject is gedefinieerd, is het een publiek item
      if (!item.subject) {
        return true;
      }

      // Check of de gebruiker read rechten heeft
      return this.abilityService.can('read', item.subject);
    });
  });

  async onMenuItem(menuItem: Ad6MenuItem): Promise<void> {
    if (menuItem.data) {
      await this.router.navigate(menuItem.data);
    }
  }
}
